/**
 * 3-axis radar (Constraint Match / Availability / Expectation).
 * Renders the latest snapshot as the base polygon and lets the user drag the
 * three sliders to preview a "what-if" overall — display only; the snapshot
 * itself is server-owned and not edited from here.
 */

import { useState } from "react";

interface Dims {
  constraintMatch: number;
  availability: number;
  expectation: number;
}

const W = 280;
const H = 280;
const CX = W / 2;
const CY = H / 2;
const R = 100;

// Three axes: 0=top, 120°=lower-right, 240°=lower-left.
const ANGLES = [-Math.PI / 2, -Math.PI / 2 + (2 * Math.PI) / 3, -Math.PI / 2 + (4 * Math.PI) / 3];
const LABELS = ["Constraint Match", "Availability", "Expectation"];
// Hover copy per axis — canonical terminology only (docs/specs/discipline-rating-fit-score.md
// Story E1 AC2). Expectation reads each Primary's Discipline Rating (1–5) against the
// Solution's Expectation Level (1–5) — never a competency/A–D grade.
const AXIS_TOOLTIPS = [
  "Constraint Match — RM-confirmed Constraint ratings, points-weighted across Primaries.",
  "Availability — capacity coverage: Primary + Supporting points vs Intensity.",
  "Expectation — Discipline Rating (1–5) vs Expectation Level (1–5) per Primary, points-weighted. See the breakdown below for every Primary's contribution.",
];

function pointAt(angle: number, dist: number) {
  return [CX + Math.cos(angle) * dist, CY + Math.sin(angle) * dist] as const;
}

function polygon(dims: number[]) {
  return ANGLES.map((a, i) => pointAt(a, ((dims[i] ?? 0) / 100) * R).join(",")).join(" ");
}

function classify(score: number, hasR1: boolean) {
  if (!hasR1) return "At Risk";
  if (score >= 75) return "Strong";
  if (score >= 50) return "Moderate";
  return "At Risk";
}

export function FitRadar({
  snapshot,
  hasR1,
}: {
  snapshot: (Dims & { overall: number; tier: string }) | null;
  hasR1: boolean;
}) {
  const base = snapshot ?? {
    constraintMatch: 0,
    availability: 0,
    expectation: 0,
    overall: 0,
    tier: "At Risk",
  };
  const [preview, setPreview] = useState<Dims>({
    constraintMatch: base.constraintMatch,
    availability: base.availability,
    expectation: base.expectation,
  });

  const previewOverall =
    0.5 * preview.constraintMatch + 0.25 * preview.availability + 0.25 * preview.expectation;
  const previewTier = classify(previewOverall, hasR1);

  const baseDims = [base.constraintMatch, base.availability, base.expectation];
  const previewDims = [preview.constraintMatch, preview.availability, preview.expectation];

  return (
    <div className="grid gap-4 md:grid-cols-[280px_1fr]">
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="h-72 w-72">
          <title>Fit Score radar</title>
          {/* Rings at 25/50/75/100. */}
          {[25, 50, 75, 100].map((pct) => (
            <polygon
              key={pct}
              points={ANGLES.map((a) => pointAt(a, (pct / 100) * R).join(",")).join(" ")}
              fill="none"
              stroke="var(--border)"
              strokeWidth="1"
              strokeDasharray={pct === 100 ? "0" : "2 3"}
            />
          ))}
          {/* Axis lines. */}
          {ANGLES.map((a, i) => {
            const [x, y] = pointAt(a, R);
            return (
              <line
                key={LABELS[i]}
                x1={CX}
                y1={CY}
                x2={x}
                y2={y}
                stroke="var(--border)"
                strokeWidth="1"
              />
            );
          })}
          {/* Snapshot polygon. */}
          <polygon
            points={polygon(baseDims)}
            fill="var(--brand-600)"
            fillOpacity="0.18"
            stroke="var(--brand-600)"
            strokeWidth="1.5"
          />
          {/* Preview polygon. */}
          <polygon
            points={polygon(previewDims)}
            fill="var(--intensity-8)"
            fillOpacity="0.25"
            stroke="var(--intensity-8)"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
          {/* Axis labels. */}
          {ANGLES.map((a, i) => {
            const [x, y] = pointAt(a, R + 18);
            return (
              <text
                key={`label-${LABELS[i]}`}
                x={x}
                y={y}
                fontSize="10"
                fill="var(--foreground)"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}
              >
                <title>{AXIS_TOOLTIPS[i]}</title>
                {LABELS[i]}
              </text>
            );
          })}
        </svg>
        <div className="absolute right-2 top-2 flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1 text-[10px]">
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-brand-600" /> Snapshot
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-[var(--intensity-8)]" /> Preview
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Snapshot
            </div>
            <div className="font-display text-[20px] font-bold leading-none">
              {Math.round(base.overall)}%
            </div>
            <div className="text-[11px] text-muted-foreground">{base.tier}</div>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Preview
            </div>
            <div className="font-display text-[20px] font-bold leading-none">
              {Math.round(previewOverall)}%
            </div>
            <div className="text-[11px] text-muted-foreground">{previewTier}</div>
          </div>
        </div>

        {(["constraintMatch", "availability", "expectation"] as const).map((k, i) => (
          <label key={k} className="block">
            <div className="mb-0.5 flex items-center justify-between text-[11px]">
              <span className="font-semibold" title={AXIS_TOOLTIPS[i]}>
                {LABELS[i]}
              </span>
              <span className="text-muted-foreground">
                {Math.round(preview[k])} (snap {Math.round(baseDims[i] ?? 0)})
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={preview[k]}
              onChange={(e) => setPreview((p) => ({ ...p, [k]: Number(e.target.value) }))}
              className="w-full accent-[var(--intensity-8)]"
            />
          </label>
        ))}

        <p className="text-[10px] text-muted-foreground">
          Snapshot polygon is the last persisted Fit Score (server-owned, recomputed on every
          assignment change). Preview is local — does not write.
        </p>
      </div>
    </div>
  );
}
